import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PolicyConfigurationForm } from './PolicyConfigurationForm';
import { useLeavePolicyMutations } from '@/hooks/leave';
import { useToast } from '@/hooks/use-toast';
import type { LeavePolicyWithRules, UpdateLeavePolicyInput } from '@/services/leave/leavePolicyTypes';

interface EditPolicyDialogProps {
  policy: LeavePolicyWithRules | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditPolicyDialog({ policy, open, onOpenChange }: EditPolicyDialogProps) {
  const { updateLeavePolicy } = useLeavePolicyMutations();
  const { toast } = useToast();

  if (!policy) return null;

  const handleSave = async (data: UpdateLeavePolicyInput) => {
    console.log('[EditPolicyDialog] ===== SAVE HANDLER CALLED =====');
    console.log('[EditPolicyDialog] Received data from form:', data);
    console.log('[EditPolicyDialog] Policy ID:', policy.id);
    
    // Enhanced validation
    if (!data.name?.trim()) {
      console.error('[EditPolicyDialog] Validation failed: Policy name is required');
      toast({
        title: "Validation Error",
        description: "Policy name is required",
        variant: "destructive",
      });
      return;
    }
    
    if (!data.leave_type) {
      console.error('[EditPolicyDialog] Validation failed: Leave type is required');
      toast({
        title: "Validation Error", 
        description: "Leave type is required",
        variant: "destructive",
      });
      return;
    }
    
    if (!data.accrual_method) {
      console.error('[EditPolicyDialog] Validation failed: Accrual method is required');
      toast({
        title: "Validation Error",
        description: "Accrual method is required", 
        variant: "destructive",
      });
      return;
    }
    
    try {
      console.log('[EditPolicyDialog] ===== CALLING MUTATION =====');
      console.log('[EditPolicyDialog] Mutation input:', { id: policy.id, input: data });
      
      const result = await updateLeavePolicy.mutateAsync({ id: policy.id, input: data });
      
      console.log('[EditPolicyDialog] ===== MUTATION SUCCESS =====');
      console.log('[EditPolicyDialog] Mutation result:', result);
      
      // Success toast
      toast({
        title: "Success",
        description: "Policy updated successfully",
        variant: "default",
      });
      
      console.log('[EditPolicyDialog] Policy update successful, closing dialog');
      onOpenChange(false);
    } catch (error) {
      // Enhanced error handling - error toast is already handled by mutation
      console.error('[EditPolicyDialog] ===== MUTATION ERROR =====');
      console.error('[EditPolicyDialog] Failed to update policy:', error);
      
      // Additional error toast for user feedback
      toast({
        title: "Update Failed",
        description: error instanceof Error ? error.message : "Failed to update policy. Please try again.",
        variant: "destructive",
      });
      
      // Don't close dialog on error so user can retry
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  // Transform policy data for form initial values
  const initialData = {
    name: policy.name,
    description: policy.description,
    leave_type: policy.leave_type,
    accrual_method: policy.accrual_method,
    accrual_rule: policy.accrual_rule ? {
      // Current accrual method fields
      does_not_deduct: policy.accrual_rule.does_not_deduct,
      hourly_accrual_type: policy.accrual_rule.hourly_accrual_type,
      accrual_rate: policy.accrual_rule.accrual_rate,
      accrual_rate_type: policy.accrual_rule.accrual_rate_type,
      include_overtime_hours: policy.accrual_rule.include_overtime_hours,
      threshold_hours_to_work: policy.accrual_rule.threshold_hours_to_work,
      threshold_leave_amount: policy.accrual_rule.threshold_leave_amount,
      threshold_leave_unit: policy.accrual_rule.threshold_leave_unit,
      fixed_accrual_amount: policy.accrual_rule.fixed_accrual_amount,
      fixed_accrual_unit: policy.accrual_rule.fixed_accrual_unit,
      fixed_accrual_frequency: policy.accrual_rule.fixed_accrual_frequency,
      fixed_accrual_release_type: policy.accrual_rule.fixed_accrual_release_type,
      fixed_accrual_specific_date: policy.accrual_rule.fixed_accrual_specific_date,
      fixed_accrual_pro_rata: policy.accrual_rule.fixed_accrual_pro_rata,
      enable_pro_rata: !!(policy.accrual_rule.pro_rata_start_date && policy.accrual_rule.pro_rata_start_date.length > 0),
      pro_rata_start_date: policy.accrual_rule.pro_rata_start_date,
      enable_tenure_rules: !!(policy.accrual_rule.tenure_rules && Array.isArray(policy.accrual_rule.tenure_rules) && policy.accrual_rule.tenure_rules.length > 0),
      tenure_rules: policy.accrual_rule.tenure_rules,
      max_carry_over_days: policy.accrual_rule.max_carry_over_days,
      max_carry_over_hours: policy.accrual_rule.max_carry_over_hours,
      balance_cap_days: policy.accrual_rule.balance_cap_days,
      balance_cap_hours: policy.accrual_rule.balance_cap_hours,
      allow_negative_balance: policy.accrual_rule.allow_negative_balance,
      max_negative_days: policy.accrual_rule.max_negative_days,
      negative_balance_action: policy.accrual_rule.negative_balance_action,
      // Rollover management fields
      rollover_type: policy.accrual_rule.rollover_type,
      rollover_amount_type: policy.accrual_rule.rollover_amount_type,
      rollover_date_type: policy.accrual_rule.rollover_date_type,
      rollover_partial_amount: policy.accrual_rule.rollover_partial_amount,
      rollover_specific_date: policy.accrual_rule.rollover_specific_date,
      // Max yearly accrual fields - derive enable flag from actual value
      enable_max_yearly_accrual: !!(policy.accrual_rule.max_yearly_accrual_days && policy.accrual_rule.max_yearly_accrual_days > 0),
      max_yearly_accrual_days: policy.accrual_rule.max_yearly_accrual_days,
      // Balance cap fields  
      enable_balance_cap: !!(policy.accrual_rule.balance_cap_days && policy.accrual_rule.balance_cap_days > 0),
    } : undefined,
    approver_profile_ids: policy.approvers?.map(a => a.approver_profile_id) || []
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Edit Policy: {policy.name}</DialogTitle>
          <DialogDescription>
            Update the configuration for this leave policy. Changes will apply to future 
            leave requests and calculations.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[70vh] pr-4">
          <div className="space-y-4">
            {updateLeavePolicy.isPending && (
              <div className="bg-muted/50 p-3 rounded-md border border-muted">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                  Updating policy...
                </div>
              </div>
            )}
            <PolicyConfigurationForm
              initialData={initialData}
              onSave={handleSave}
              onCancel={handleCancel}
              isLoading={updateLeavePolicy.isPending}
              mode="edit"
            />
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}